import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	Timestamp,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	query,
	updateDoc,
	where,
	writeBatch,
} from 'firebase/firestore';
import { chunk } from 'lodash';
import BASE_QUERY from '@/features/baseQuery';
import { auth, firestore, toBrokerGroupRefs } from '@/utils/firebase';
import { getActingUser } from '@/features/user/userApi';

import {
	toUserRef,
	normalizeDesignJson,
	serializeDesignJsonForStorage,
	parseDesignJsonFromStorage,
	toError,
	serializeTemplateSummary,
	serializeTemplateDetail,
} from '../unlayerEmailTemplates/unlayerEmailTemplatesApi';
import { AI_EMAIL_WORKFLOW_TYPES } from '@/features/aiWorkflow/constants';

const UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION = 'unlayerAiEmailTemplates';

const canAccessAiTemplate = (
	template,
	{ brokerGroupIds = [], ownerDocId, isFolioAdmin = false },
) => {
	if (isFolioAdmin) {
		return true;
	}

	if (template?.master) {
		return true;
	}

	const isOwner = ownerDocId != null && String(template?.ownerDocId) === String(ownerDocId);

	if (isOwner) {
		const templateBrokerGroupIds = (template?.brokerGroupIds ?? []).map(String).filter(Boolean);
		if (templateBrokerGroupIds.length === 0) {
			return true;
		}

		const allowedBrokerGroupSet = new Set(brokerGroupIds.map(String));
		return templateBrokerGroupIds.some((brokerGroupId) =>
			allowedBrokerGroupSet.has(brokerGroupId),
		);
	}

	const allowedBrokerGroupSet = new Set(brokerGroupIds.map(String));
	return (template?.brokerGroupIds ?? [])
		.map(String)
		.some((brokerGroupId) => allowedBrokerGroupSet.has(brokerGroupId));
};

const resolveBrokerGroupIds = (templateData = {}, fallbackBrokerGroupIds = []) => {
	const storedBrokerGroupIds = (templateData.brokerGroupIds ?? []).map(String).filter(Boolean);
	if (storedBrokerGroupIds.length > 0) {
		return storedBrokerGroupIds;
	}

	const refBrokerGroupIds = (templateData.brokerGroups ?? [])
		.map((ref) => ref?.id)
		.filter(Boolean);
	if (refBrokerGroupIds.length > 0) {
		return refBrokerGroupIds;
	}

	return (fallbackBrokerGroupIds ?? []).map(String).filter(Boolean);
};

export const serializeAiTemplateSummary = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};

	return {
		...serializeTemplateSummary(templateDoc),
		brokerGroupIds: resolveBrokerGroupIds(data),
		type: data?.type ?? '',
		workflowType: data?.workflowType ?? '',
		active: Boolean(data?.active),
		master: Boolean(data?.master),
	};
};

export const serializeAiTemplateDetail = (templateDoc) => {
	const data = templateDoc?.data?.() ?? {};
	return {
		...serializeTemplateDetail(templateDoc),
		brokerGroupIds: resolveBrokerGroupIds(data),
		type: data?.type ?? '',
		workflowType: data?.workflowType ?? '',
		active: Boolean(data?.active),
		master: Boolean(data?.master),
	};
};

const normalizeWorkflowType = (workflowType) => {
	if (!workflowType || String(workflowType).toLowerCase() === 'none') {
		return '';
	}

	return AI_EMAIL_WORKFLOW_TYPES[workflowType] ?? String(workflowType);
};

const buildAiTemplatePayload = ({
	name,
	subject = '',
	designJson,
	userId,
	brokerGroupIds = [],
	type,
	workflowType = '',
	active = false,
	master = false,
	timestamp = Timestamp.now(),
}) => ({
	name: String(name).trim(),
	subject: String(subject),
	designJson: serializeDesignJsonForStorage(designJson),
	type: String(type ?? '').trim(),
	active: Boolean(active),
	master: Boolean(master),
	workflowType: normalizeWorkflowType(workflowType),
	createdAt: timestamp,
	updatedAt: timestamp,
	createdWhen: timestamp,
	lastUpdated: timestamp,
	createdBy: toUserRef(userId),
	brokerGroups: toBrokerGroupRefs(brokerGroupIds),
	brokerGroupIds: (brokerGroupIds ?? []).map(String),
	ownerDocId: userId,
});

/**
 * Deactivates other active AI templates with the same workflowType that either:
 * - share at least one broker group with the source template (when includeSharedOverlaps), or
 * - have no broker groups (unshared) and belong to the signed-in user, including older docs
 *   missing workflowType/brokerGroupIds.
 * Queries without a workflowType constraint, then filters in memory.
 */
const deactivateConflictingAiTemplates = async ({
	templateId,
	brokerGroupIds = [],
	workflowType = '',
	includeSharedOverlaps = true,
}) => {
	const scopedBrokerGroupIds = (brokerGroupIds ?? []).map(String).filter(Boolean);
	const normalizedWorkflowType = normalizeWorkflowType(workflowType);
	const scopedBrokerGroupSet = new Set(scopedBrokerGroupIds);
	const collectionRef = collection(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION);
	const docsToDeactivate = new Map();

	const considerTemplateDoc = (templateDoc, { allowUnshared, allowSharedOverlap }) => {
		if (templateDoc.id === templateId || templateDoc.data()?.master) {
			return;
		}

		const templateData = templateDoc.data() ?? {};
		const docWorkflowType = normalizeWorkflowType(templateData.workflowType);
		if (docWorkflowType !== normalizedWorkflowType) {
			return;
		}

		const docBrokerGroupIds = resolveBrokerGroupIds(templateData);
		const isUnshared = docBrokerGroupIds.length === 0;
		if (isUnshared) {
			if (allowUnshared) {
				docsToDeactivate.set(templateDoc.id, templateDoc.ref);
			}
			return;
		}

		if (!allowSharedOverlap || scopedBrokerGroupIds.length === 0) {
			return;
		}

		const sharesBrokerGroup = docBrokerGroupIds.some((brokerGroupId) =>
			scopedBrokerGroupSet.has(brokerGroupId),
		);
		if (sharesBrokerGroup) {
			docsToDeactivate.set(templateDoc.id, templateDoc.ref);
		}
	};

	if (includeSharedOverlaps && scopedBrokerGroupIds.length > 0) {
		const brokerGroupChunks = chunk(scopedBrokerGroupIds, 30);

		for (const brokerGroupChunk of brokerGroupChunks) {
			const snapshot = await getDocs(
				query(
					collectionRef,
					where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
					where('active', '==', true),
				),
			);

			snapshot.docs.forEach((templateDoc) =>
				considerTemplateDoc(templateDoc, {
					allowUnshared: false,
					allowSharedOverlap: true,
				}),
			);
		}
	}

	// Secondary pass: active templates with no broker groups (and missing-field docs)
	// are not returned by array-contains-any, so fetch actives and filter in memory. An
	// unshared template is only visible to its owner (Firestore rules), so only the signed-in
	// user's are fetched.
	const activeSnapshot = await getDocs(
		query(
			collectionRef,
			where('ownerDocId', '==', auth.currentUser?.uid),
			where('active', '==', true),
		),
	);
	activeSnapshot.docs.forEach((templateDoc) =>
		considerTemplateDoc(templateDoc, {
			allowUnshared: true,
			allowSharedOverlap: includeSharedOverlaps,
		}),
	);

	if (docsToDeactivate.size === 0) {
		return;
	}

	const batch = writeBatch(firestore);

	docsToDeactivate.forEach((templateRef) => {
		batch.update(templateRef, {
			active: false,
		});
	});

	await batch.commit();
};

const assertCanActivateAiTemplate = (brokerGroupIds = []) => {
	const resolvedBrokerGroupIds = (brokerGroupIds ?? []).map(String).filter(Boolean);
	if (resolvedBrokerGroupIds.length === 0) {
		throw new Error('Share the template with at least one broker group before setting it active');
	}
};

export const unlayerAiEmailTemplatesApi = createApi({
	reducerPath: 'unlayerAiEmailTemplatesApi',
	tagTypes: ['UNLAYER_AI_EMAIL_TEMPLATES', 'UNLAYER_AI_EMAIL_TEMPLATE'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		getUnlayerAiEmailTemplateSummaries: builder.query({
			providesTags: ['UNLAYER_AI_EMAIL_TEMPLATES'],
			// Always resolves the logged-in user itself — this list is never fetched on
			// behalf of anyone else, so there's no caller-supplied owner/brokerGroup args.
			queryFn: async (_arg, { dispatch, getState }) => {
				try {
					const performer = await getActingUser(dispatch, getState);
					const brokerGroupIds = performer?.brokerGroupArray ?? [];
					const ownerDocId = performer?.userId ?? null;
					const isFolioAdmin = Boolean(performer?.isFolioAdmin);

					const collectionRef = collection(
						firestore,
						UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION,
					);
					const templateMap = new Map();

					if (brokerGroupIds.length > 0) {
						const brokerGroupChunks = chunk(brokerGroupIds.map(String), 30);

						for (const brokerGroupChunk of brokerGroupChunks) {
							const snapshot = await getDocs(
								query(
									collectionRef,
									where('brokerGroupIds', 'array-contains-any', brokerGroupChunk),
								),
							);

							snapshot.docs.forEach((templateDoc) => {
								templateMap.set(
									templateDoc.id,
									serializeAiTemplateSummary(templateDoc),
								);
							});
						}
					}

					if (ownerDocId != null) {
						const ownerDocSnapshot = await getDocs(
							query(collectionRef, where('ownerDocId', '==', ownerDocId)),
						);

						ownerDocSnapshot.docs.forEach((templateDoc) => {
							templateMap.set(
								templateDoc.id,
								serializeAiTemplateSummary(templateDoc),
							);
						});
					}

					const masterSnapshot = await getDocs(
						query(collectionRef, where('master', '==', true)),
					);

					masterSnapshot.docs.forEach((templateDoc) => {
						templateMap.set(templateDoc.id, serializeAiTemplateSummary(templateDoc));
					});

					if (templateMap.size === 0) {
						return { data: [] };
					}

					const summaries = Array.from(templateMap.values())
						.filter((template) =>
							canAccessAiTemplate(template, {
								brokerGroupIds,
								ownerDocId,
								isFolioAdmin,
							}),
						)
						.sort((a, b) => b.lastUpdated - a.lastUpdated);

					return { data: summaries };
				} catch (error) {
					console.error('Error fetching Unlayer AI template summaries', error);

					return {
						error: toError(error, 'Unable to load AI templates'),
					};
				}
			},
		}),

		getUnlayerAiEmailTemplateDetail: builder.query({
			providesTags: (result, error, { id }) => [{ type: 'UNLAYER_AI_EMAIL_TEMPLATE', id }],
			queryFn: async ({ id }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					const templateRef = doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('AI Template not found');
					}
					return {
						data: serializeAiTemplateDetail(snapshot),
					};
				} catch (error) {
					console.error('Error fetching Unlayer AI template detail', error);
					return { error: toError(error, 'Unable to load AI template') };
				}
			},
		}),

		createUnlayerAiEmailTemplate: builder.mutation({
			invalidatesTags: ['UNLAYER_AI_EMAIL_TEMPLATES'],
			queryFn: async (
				{
					name,
					subject = '',
					designJson,
					brokerGroupIds = [],
					workflowType = '',
					type,
					active = false,
				},
				{ dispatch, getState },
			) => {
				try {
					if (!name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					if (active) {
						assertCanActivateAiTemplate(brokerGroupIds);
					}

					const now = Timestamp.now();
					const payload = buildAiTemplatePayload({
						name,
						subject,
						designJson,
						userId,
						brokerGroupIds,
						type,
						workflowType,
						active,
						master: false,
						timestamp: now,
					});

					const result = await addDoc(
						collection(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION),
						payload,
					);

					if (active && !payload.master) {
						await deactivateConflictingAiTemplates({
							templateId: result.id,
							brokerGroupIds: payload.brokerGroupIds,
							workflowType: payload.workflowType,
						});
					}

					return {
						data: {
							id: result.id,
							name: payload.name,
							subject: payload.subject,
							type: payload.type,
							active: payload.active,
							master: payload.master,
							workflowType: payload.workflowType,
							designJson: normalizeDesignJson(designJson),
							createdWhen: now.toMillis(),
							lastUpdated: now.toMillis(),
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: payload.brokerGroupIds,
						},
					};
				} catch (error) {
					console.error('Error creating Unlayer AI template', error);
					return { error: toError(error, 'Unable to create AI template') };
				}
			},
		}),

		updateUnlayerAiEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_AI_EMAIL_TEMPLATE', id },
				'UNLAYER_AI_EMAIL_TEMPLATES',
			],
			queryFn: async (
				{
					id,
					name,
					subject = '',
					designJson,
					brokerGroupIds = [],
					workflowType = '',
					type,
					active = false,
				},
				{ dispatch, getState },
			) => {
				try {
					if (!id || !name) {
						throw new Error('Missing required template fields');
					}

					const performer = await getActingUser(dispatch, getState);
					const userId = performer.userId;

					const templateRef = doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id);
					const existingSnapshot = await getDoc(templateRef);
					if (!existingSnapshot.exists()) {
						throw new Error('AI Template not found');
					}

					const resolvedBrokerGroupIds = (brokerGroupIds ?? [])
						.map(String)
						.filter(Boolean);

					if (active) {
						assertCanActivateAiTemplate(resolvedBrokerGroupIds);
					}

					const lastUpdated = Timestamp.now();
					const resolvedWorkflowType = normalizeWorkflowType(workflowType);
					const payload = {
						name: String(name).trim(),
						subject: String(subject),
						designJson: serializeDesignJsonForStorage(designJson),
						type: String(type).trim(),
						active: Boolean(active),
						master: Boolean(existingSnapshot.data().master),
						workflowType: resolvedWorkflowType,
						lastUpdated,
						updatedAt: lastUpdated,
						createdBy: toUserRef(userId),
						brokerGroups: toBrokerGroupRefs(resolvedBrokerGroupIds),
						brokerGroupIds: resolvedBrokerGroupIds,
						ownerDocId: userId,
					};

					await updateDoc(templateRef, payload);

					if (active && !payload.master) {
						await deactivateConflictingAiTemplates({
							templateId: id,
							brokerGroupIds: resolvedBrokerGroupIds,
							workflowType: resolvedWorkflowType,
						});
					}

					return {
						data: {
							id,
							name: payload.name,
							subject: payload.subject,
							type: payload.type,
							active: payload.active,
							master: payload.master,
							designJson: normalizeDesignJson(designJson),
							lastUpdated: lastUpdated.toMillis(),
							workflowType: resolvedWorkflowType,
							ownerDocId: userId,
							createdById: String(userId),
							brokerGroupIds: payload.brokerGroupIds,
						},
					};
				} catch (error) {
					console.error('Error updating Unlayer AI template', error);
					return { error: toError(error, 'Unable to update AI template') };
				}
			},
		}),
		updateUnlayerAiEmailTemplateProperties: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_AI_EMAIL_TEMPLATE', id },
				'UNLAYER_AI_EMAIL_TEMPLATES',
			],
			queryFn: async ({ id, properties = {} }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}

					const updates = Object.fromEntries(
						Object.entries(properties).filter(([, value]) => value !== undefined),
					);

					if (Object.keys(updates).length === 0) {
						return { data: { id } };
					}

					if (Object.prototype.hasOwnProperty.call(updates, 'workflowType')) {
						updates.workflowType = normalizeWorkflowType(updates.workflowType);
					}

					const templateRef = doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id);
					const snapshot = await getDoc(templateRef);
					if (!snapshot.exists()) {
						throw new Error('AI Template not found');
					}

					const templateData = snapshot.data() ?? {};
					if (templateData.master) {
						throw new Error('Master templates cannot be edited');
					}

					const lastUpdated = Timestamp.now();
					await updateDoc(templateRef, {
						...updates,
						lastUpdated,
						updatedAt: lastUpdated,
					});

					const nextWorkflowType = Object.prototype.hasOwnProperty.call(
						updates,
						'workflowType',
					)
						? updates.workflowType
						: normalizeWorkflowType(templateData.workflowType);
					const nextActive = Object.prototype.hasOwnProperty.call(updates, 'active')
						? Boolean(updates.active)
						: Boolean(templateData.active);
					const nextBrokerGroupIds = Object.prototype.hasOwnProperty.call(
						updates,
						'brokerGroupIds',
					)
						? (updates.brokerGroupIds ?? []).map(String).filter(Boolean)
						: resolveBrokerGroupIds(templateData);

					if (nextActive) {
						assertCanActivateAiTemplate(nextBrokerGroupIds);
						await deactivateConflictingAiTemplates({
							templateId: id,
							brokerGroupIds: nextBrokerGroupIds,
							workflowType: nextWorkflowType,
						});
					}

					return { data: { id, ...updates } };
				} catch (error) {
					console.error('Error updating Unlayer AI template properties', error);
					return { error: toError(error, 'Unable to update AI template') };
				}
			},
		}),
		setActiveUnlayerAiEmailTemplate: builder.mutation({
			invalidatesTags: ['UNLAYER_AI_EMAIL_TEMPLATES'],
			queryFn: async ({ id, brokerGroupIds = [], workflowType, active = true }) => {
				try {
					if (!id) {
						throw new Error('Missing required template fields');
					}

					const templateRef = doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id);
					const templateSnapshot = await getDoc(templateRef);
					if (!templateSnapshot.exists()) {
						throw new Error('AI Template not found');
					}

					const templateData = templateSnapshot.data() ?? {};
					if (templateData.master) {
						throw new Error('Master templates cannot be set active');
					}

					const resolvedBrokerGroupIds = resolveBrokerGroupIds(
						templateData,
						brokerGroupIds,
					);
					const resolvedWorkflowType = normalizeWorkflowType(
						workflowType ?? templateData.workflowType,
					);

					if (active) {
						assertCanActivateAiTemplate(resolvedBrokerGroupIds);
						await deactivateConflictingAiTemplates({
							templateId: id,
							brokerGroupIds: resolvedBrokerGroupIds,
							workflowType: resolvedWorkflowType,
						});
					}

					await updateDoc(templateRef, {
						active: Boolean(active),
					});

					return {
						data: {
							id,
							active: Boolean(active),
							workflowType: resolvedWorkflowType,
						},
					};
				} catch (error) {
					console.error('Error setting active Unlayer AI template', error);
					return { error: toError(error, 'Unable to update active AI template') };
				}
			},
		}),

		shareUnlayerAiEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_AI_EMAIL_TEMPLATE', id },
				'UNLAYER_AI_EMAIL_TEMPLATES',
			],
			queryFn: async ({ id, brokerGroupIds = [] }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}

					const templateRef = doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id);
					const templateSnapshot = await getDoc(templateRef);
					if (!templateSnapshot.exists()) {
						throw new Error('AI Template not found');
					}

					const templateData = templateSnapshot.data() ?? {};
					if (templateData.master) {
						throw new Error('Master templates cannot be shared');
					}

					const resolvedBrokerGroupIds = (brokerGroupIds ?? [])
						.map(String)
						.filter(Boolean);
					const lastUpdated = Timestamp.now();

					await updateDoc(templateRef, {
						brokerGroupIds: resolvedBrokerGroupIds,
						brokerGroups: toBrokerGroupRefs(resolvedBrokerGroupIds),
						lastUpdated,
						updatedAt: lastUpdated,
					});

					if (resolvedBrokerGroupIds.length > 0) {
						await deactivateConflictingAiTemplates({
							templateId: id,
							brokerGroupIds: resolvedBrokerGroupIds,
							workflowType: templateData.workflowType,
							// Sharing always clears unshared actives of this workflow type.
							// Only deactivate overlapping shared actives if this template is active.
							includeSharedOverlaps: Boolean(templateData.active),
						});
					} else if (templateData.active) {
						await updateDoc(templateRef, {
							active: false,
						});
					}

					return {
						data: {
							id,
							brokerGroupIds: resolvedBrokerGroupIds,
						},
					};
				} catch (error) {
					console.error('Error sharing Unlayer AI template', error);
					return { error: toError(error, 'Unable to update AI template sharing') };
				}
			},
		}),

		deleteUnlayerAiEmailTemplate: builder.mutation({
			invalidatesTags: (result, error, { id }) => [
				{ type: 'UNLAYER_AI_EMAIL_TEMPLATE', id },
				'UNLAYER_AI_EMAIL_TEMPLATES',
			],
			queryFn: async ({ id }) => {
				try {
					if (!id) {
						throw new Error('Template id is required');
					}
					await deleteDoc(doc(firestore, UNLAYER_AI_EMAIL_TEMPLATES_COLLECTION, id));
					return { data: { id } };
				} catch (error) {
					console.error('Error deleting Unlayer AI template', error);
					return { error: toError(error, 'Unable to delete AI template') };
				}
			},
		}),
	}),
});

export const {
	useLazyGetUnlayerAiEmailTemplateSummariesQuery,
	useLazyGetUnlayerAiEmailTemplateDetailQuery,
	useCreateUnlayerAiEmailTemplateMutation,
	useUpdateUnlayerAiEmailTemplateMutation,
	useSetActiveUnlayerAiEmailTemplateMutation,
	useShareUnlayerAiEmailTemplateMutation,
	useDeleteUnlayerAiEmailTemplateMutation,
	useUpdateUnlayerAiEmailTemplatePropertiesMutation,
} = unlayerAiEmailTemplatesApi;
